/**
 * Vendor store-type + pipeline-stage chip palettes — the single source of truth
 * for the Vendor-applications screens, matching the Figma deck (list 273:2,
 * review 295:306, board 300:2).
 *
 * Store-type colors are borrowed from the consumer `departmentConfig` per the
 * locked taxonomy (store types are their OWN list, NOT department mappings — we
 * only reuse the colors). Dessert + Coffee are off-palette. Chip style = a light
 * tint of the color as background + the color (darkened for light hues) as text.
 */

export interface ChipColors {
  label: string;
  /** Foreground (text) color. */
  fg: string;
  /** Background (light tint) color. */
  bg: string;
}

function hexToRgb(hex: string): [number, number, number] {
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
}

/** Mix a hex color toward white. frac=1 keeps the color, frac=0 is white. */
function tint(hex: string, frac: number): string {
  const [r, g, b] = hexToRgb(hex);
  const mix = (c: number) => Math.round(c * frac + 255 * (1 - frac));
  return `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`;
}

/** Darken a hex color (for readable text on a light tint of a pale hue). */
function darken(hex: string, frac: number): string {
  const [r, g, b] = hexToRgb(hex);
  return `rgb(${Math.round(r * frac)}, ${Math.round(g * frac)}, ${Math.round(b * frac)})`;
}

interface StoreTypeDef {
  label: string;
  color: string;
  /** Pale hue → darken the text + use a stronger bg tint so it stays legible. */
  light?: boolean;
}

// Keyed by normalized businessType. Aliases handled in storeTypeChip().
const STORE_TYPES: Record<string, StoreTypeDef> = {
  grocery: { label: 'Grocery', color: '#2B343E' },
  produce: { label: 'Produce', color: '#00AA00' },
  farmers_market: { label: "Farmer's market", color: '#00AA00' },
  butcher: { label: 'Butcher', color: '#BD001F' },
  seafood: { label: 'Seafood', color: '#0F25A1' },
  bakery: { label: 'Bakery', color: '#753226' },
  deli: { label: 'Deli', color: '#E36A3F' },
  dessert: { label: 'Dessert', color: '#EC4899' },
  coffee: { label: 'Coffee', color: '#4A2C1A' },
  alcohol: { label: 'Alcohol', color: '#831F49' },
  natural_foods: { label: 'Natural foods', color: '#C0CAAA', light: true },
  drug: { label: 'Drug store', color: '#708238' },
  convenience: { label: 'Convenience', color: '#07BDC4' },
  other: { label: 'Other', color: '#737070' },
};

const STORE_TYPE_ALIASES: Record<string, string> = {
  'farmers market': 'farmers_market',
  "farmer's market": 'farmers_market',
  produce_farmers_market: 'farmers_market',
  drug_store: 'drug',
  drugstore: 'drug',
  pharmacy: 'drug',
  convenience_store: 'convenience',
  natural: 'natural_foods',
  coop: 'natural_foods',
  'co-op': 'natural_foods',
  'coffee / tea roaster': 'coffee',
  tea: 'coffee',
  specialty: 'deli', // legacy bucket folded into Deli
};

/** Resolve a (possibly messy) businessType string to its chip colors. */
export function storeTypeChip(businessType: string | null | undefined): ChipColors {
  const raw = (businessType ?? '').trim().toLowerCase().replace(/[\s/]+/g, '_');
  const key = STORE_TYPES[raw] ? raw : (STORE_TYPE_ALIASES[raw] ?? STORE_TYPE_ALIASES[businessType?.trim().toLowerCase() ?? ''] ?? raw);
  const def = STORE_TYPES[key] ?? { label: businessType || 'Unknown', color: '#737070' };
  return {
    label: def.label,
    fg: def.light ? darken(def.color, 0.45) : def.color,
    bg: tint(def.color, def.light ? 0.5 : 0.16),
  };
}

// ─── Pipeline stages ──────────────────────────────────────────────────────────

export type VendorAppStatus =
  | 'submitted'
  | 'in_review'
  | 'data_shape_assigned'
  | 'staff_visit_scheduled'
  | 'adapter_built'
  | 'vendor_confirmed'
  | 'approved'
  | 'declined'
  | 'withdrawn';

/** Ordered, labelled pipeline stages shown in the stepper + board columns. */
export const PIPELINE_STAGES: { status: VendorAppStatus; label: string }[] = [
  { status: 'submitted', label: 'Submitted' },
  { status: 'in_review', label: 'In review' },
  { status: 'data_shape_assigned', label: 'Data shape' },
  { status: 'staff_visit_scheduled', label: 'Staff visit' },
  { status: 'adapter_built', label: 'Adapter built' },
  { status: 'vendor_confirmed', label: 'Confirmed' },
  { status: 'approved', label: 'Approved' },
];

export const TERMINAL_STAGES: { status: VendorAppStatus; label: string }[] = [
  { status: 'declined', label: 'Declined' },
  { status: 'withdrawn', label: 'Withdrawn' },
];

const STAGE_CHIPS: Record<VendorAppStatus, ChipColors> = {
  submitted: { label: 'Submitted', bg: '#E2E8F0', fg: '#475569' },
  in_review: { label: 'In review', bg: '#DBEAFE', fg: '#1D4ED8' },
  data_shape_assigned: { label: 'Data shape', bg: '#E0E7FF', fg: '#4338CA' },
  staff_visit_scheduled: { label: 'Staff visit', bg: '#FEF3C7', fg: '#B45309' },
  adapter_built: { label: 'Adapter built', bg: '#CCFBF1', fg: '#0F766E' },
  vendor_confirmed: { label: 'Confirmed', bg: '#D1FAE5', fg: '#047857' },
  approved: { label: 'Approved', bg: '#DCFCE7', fg: '#15803D' },
  declined: { label: 'Declined', bg: '#FEE2E2', fg: '#B91C1C' },
  withdrawn: { label: 'Withdrawn', bg: '#E2E8F0', fg: '#64748B' },
};

export function stageChip(status: VendorAppStatus | string): ChipColors {
  return STAGE_CHIPS[status as VendorAppStatus] ?? { label: String(status), bg: '#E2E8F0', fg: '#475569' };
}

// ─── Managed-vendor lifecycle status (Vendor.status enum) ──────────────────────
// An approved vendor is NOT live to shoppers until it finishes the §9 setup
// wizard: pending → onboarding (Setup) → active (Live). `suspended` hides the
// storefront. Mirrors the Prisma `VendorStatus` enum (public.vendor_status).

export type VendorStatus =
  | 'pending'
  | 'onboarding'
  | 'active'
  | 'inactive'
  | 'suspended'
  | 'deactivated'
  | 'blocked';

const VENDOR_STATUS_CHIPS: Record<VendorStatus, ChipColors> = {
  pending: { label: 'Pending', bg: '#DBEAFE', fg: '#1D4ED8' },
  onboarding: { label: 'Onboarding', bg: '#FEF3C7', fg: '#B45309' },
  active: { label: 'Active', bg: '#DCFCE7', fg: '#15803D' },
  inactive: { label: 'Inactive', bg: '#E2E8F0', fg: '#475569' },
  suspended: { label: 'Suspended', bg: '#FEE2E2', fg: '#B91C1C' },
  deactivated: { label: 'Deactivated', bg: '#E2E8F0', fg: '#64748B' },
  blocked: { label: 'Blocked', bg: '#FEE2E2', fg: '#991B1B' },
};

export function vendorStatusChip(status: VendorStatus | string): ChipColors {
  return VENDOR_STATUS_CHIPS[status as VendorStatus] ?? { label: String(status), bg: '#E2E8F0', fg: '#475569' };
}

/** True for a vendor that is live to shoppers (`active`). */
export function isVendorLive(status: VendorStatus | string): boolean {
  return status === 'active';
}

/** Stage index in the linear pipeline (-1 for terminal/unknown). */
export function stageIndex(status: VendorAppStatus | string): number {
  return PIPELINE_STAGES.findIndex((s) => s.status === status);
}
